import apiClient from '@/utils/apiClient';
import { ADMIN_ENDPOINTS, BILLING_ADMIN_ENDPOINTS } from './constants';

// ─── Types ────────────────────────────────────────────────────────

export interface AdminMerchant {
    merchantId: string;
    businessName: string;
    businessEmail: string;
    status: string;
    createdAt: string;
    stores: AdminStore[];
    storeCount: number;
    features?: Record<string, boolean>;
}

export type MerchantStatus = 'active' | 'trialing' | 'suspended' | 'inactive';

export interface AdminStore {
    storeId: string;
    storeName: string;
    storeUrl: string;
    status: string;
    platform: string;
}

export interface Pagination {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
}

export interface AuditEntry {
    auditId: string;
    adminUserId: string;
    adminEmail: string;
    targetMerchantId: string;
    targetMerchantName: string;
    targetStoreId: string | null;
    action: string;
    resourceType: string | null;
    resourceId: string | null;
    requestBody: Record<string, any>;
    ipAddress: string;
    createdAt: string;
}

export interface AdminUser {
    userId: string;
    email: string;
    status: 'active' | 'inactive';
    lastLoginAt: string | null;
    createdAt: string;
}

export interface Device {
    deviceId: string;
    userId: string;
    email?: string;
    label: string;
    status: 'active' | 'pending' | 'revoked';
    lastUsedAt: string | null;
    requestedAt: string | null;
    createdAt: string;
}

// ─── API Functions ────────────────────────────────────────────────

export const adminApi = {
    listMerchants: async (params?: { page?: number; limit?: number; search?: string }) => {
        const response = await apiClient.get<{ data: { merchants: AdminMerchant[]; pagination: Pagination } }>(
            ADMIN_ENDPOINTS.MERCHANTS,
            { params }
        );
        return response.data.data;
    },

    getMerchant: async (merchantId: string) => {
        const response = await apiClient.get<{ data: AdminMerchant }>(
            ADMIN_ENDPOINTS.MERCHANT_DETAIL(merchantId)
        );
        return response.data.data;
    },

    impersonate: async (body: { merchantId: string; storeId: string }) => {
        const response = await apiClient.post<{ data: { token: string; merchantId: string; storeId: string; merchantName: string; isImpersonating: boolean } }>(
            ADMIN_ENDPOINTS.IMPERSONATE,
            body
        );
        return response.data.data;
    },

    stopImpersonation: async () => {
        const response = await apiClient.post<{ data: { token: string; isImpersonating: boolean } }>(
            ADMIN_ENDPOINTS.STOP_IMPERSONATION
        );
        return response.data.data;
    },

    getAuditLog: async (params?: {
        page?: number;
        limit?: number;
        adminUserId?: string;
        targetMerchantId?: string;
        startDate?: string;
        endDate?: string;
    }) => {
        const response = await apiClient.get<{ data: { entries: AuditEntry[]; pagination: Pagination } }>(
            ADMIN_ENDPOINTS.AUDIT_LOG,
            { params }
        );
        return response.data.data;
    },

    verifyDevice: async (fingerprint: string) => {
        const response = await apiClient.post<{ data: { verified: boolean; pendingApproval: boolean; revoked: boolean } }>(
            ADMIN_ENDPOINTS.VERIFY_DEVICE,
            { fingerprint }
        );
        return response.data.data;
    },

    listAdminUsers: async () => {
        const response = await apiClient.get<{ data: { users: AdminUser[]; cap: number } }>(
            ADMIN_ENDPOINTS.USERS
        );
        return response.data.data;
    },

    createAdminUser: async (body: { email: string; password?: string }) => {
        const response = await apiClient.post<{ data: { userId: string; email: string } }>(
            ADMIN_ENDPOINTS.USERS,
            body
        );
        return response.data.data;
    },

    updateAdminUserStatus: async (userId: string, status: 'active' | 'inactive') => {
        const response = await apiClient.patch<{ data: AdminUser }>(
            `${ADMIN_ENDPOINTS.USERS}/${userId}/status`,
            { status }
        );
        return response.data.data;
    },

    listDevices: async () => {
        const response = await apiClient.get<{ data: { devices: Device[] } }>(
            ADMIN_ENDPOINTS.DEVICES
        );
        return response.data.data;
    },

    listPendingDevices: async () => {
        const response = await apiClient.get<{ data: { devices: Device[] } }>(
            ADMIN_ENDPOINTS.DEVICES_PENDING
        );
        return response.data.data;
    },

    approveDevice: async (deviceId: string) => {
        const response = await apiClient.post<{ data: Device }>(
            `${ADMIN_ENDPOINTS.DEVICES}/${deviceId}/approve`
        );
        return response.data.data;
    },

    revokeDevice: async (deviceId: string) => {
        const response = await apiClient.post<{ data: Device }>(
            `${ADMIN_ENDPOINTS.DEVICES}/${deviceId}/revoke`
        );
        return response.data.data;
    },

    toggleMerchantFeature: async (merchantId: string, featureKey: string, enabled: boolean) => {
        const response = await apiClient.patch<{ data: { merchantId: string; features: Record<string, boolean> } }>(
            ADMIN_ENDPOINTS.MERCHANT_FEATURES(merchantId),
            { featureKey, enabled }
        );
        return response.data.data;
    },

    updateMerchantStatus: async (merchantId: string, status: MerchantStatus) => {
        const response = await apiClient.patch<{ data: AdminMerchant }>(
            ADMIN_ENDPOINTS.MERCHANT_STATUS(merchantId),
            { status }
        );
        return response.data.data;
    },
};

// ─── Billing: negotiated deals ────────────────────────────────────

export interface CataloguePlan {
    planId: string;
    code: string;
    name: string;
    description?: string;
    interval: 'monthly' | 'yearly';
    price: number;
    currency: string;
    tier?: string;
    status?: 'active' | 'inactive' | 'archived';
    isPublic: boolean;
    sortOrder?: number;
    trialDays?: number;
    features: Record<string, any>;
}

// How many stores a limit change reached. The cap it enforces lives on the store, so a
// plan edit has to be pushed down to each one.
export interface PlanFanout {
    audience: number;
    projected: number;
    deferred: number;
}

export interface CreatePlanBody {
    code: string;
    interval: 'monthly' | 'yearly';
    tier: string;
    name: string;
    description?: string;
    price: number;
    features: Record<string, any>;
    isPublic?: boolean;
    sortOrder?: number;
}

export interface UpdatePlanBody {
    name?: string;
    description?: string;
    features?: Record<string, any>;
    isPublic?: boolean;
    sortOrder?: number;
}

export interface NegotiatedDeal {
    planCode: string;
    planName: string;
    interval: 'monthly' | 'yearly';
    cataloguePrice: number;
    agreedPrice: number | null;
    isCustomPrice: boolean;
    planLimits: Record<string, any>;
    agreedLimits: Record<string, any>;
    note: string;
    expiresAt: string | null;
}

export interface BillingSubscriptionView {
    plan: { code: string; name: string; interval: string | null; features: Record<string, any> };
    status: string;
    price: number | null;
    isCustomPrice: boolean;
    isComped: boolean;
    renewsAt: string | null;
    usage: { responses: { used: number; limit: number | null } };
    limits: Record<string, number | null>;
    scheduledChange: Record<string, any> | null;
}

export interface BillingHistoryEntry {
    eventId: string;
    eventType: string;
    source: string;
    createdAt: string;
    payload: Record<string, any>;
}

export interface RecordDealBody {
    merchantId: string;
    storeId: string;
    planCode: string;
    interval: 'monthly' | 'yearly';
    customPrice?: number | null;
    featureOverrides?: Record<string, number | null>;
    note: string;
    expiresAt?: string | null;
}

export const billingAdminApi = {
    // The flag is read off the argument rather than forwarded whole: passed straight to
    // react-query as a queryFn, this receives that library's context object, and sending
    // it on as query parameters is refused by the endpoint.
    listPlans: async (options?: { includeArchived?: boolean }) => {
        const includeArchived = options?.includeArchived === true;
        const response = await apiClient.get<{ data: { plans: CataloguePlan[] } }>(
            BILLING_ADMIN_ENDPOINTS.PLANS,
            { params: includeArchived ? { includeArchived: true } : undefined }
        );
        return response.data.data.plans;
    },

    createPlan: async (body: CreatePlanBody) => {
        const response = await apiClient.post<{ data: CataloguePlan }>(
            BILLING_ADMIN_ENDPOINTS.PLANS,
            body
        );
        return response.data.data;
    },

    updatePlan: async (planId: string, body: UpdatePlanBody) => {
        const response = await apiClient.patch<{ data: { plan: CataloguePlan; fanout: PlanFanout } }>(
            BILLING_ADMIN_ENDPOINTS.PLAN(planId),
            body
        );
        return response.data.data;
    },

    // A price change writes a new row and archives the one it replaces, so existing
    // subscribers keep resolving against the price they approved.
    changePlanPrice: async (planId: string, price: number) => {
        const response = await apiClient.post<{
            data: { archived: CataloguePlan; active: CataloguePlan; carriedDeals: number };
        }>(BILLING_ADMIN_ENDPOINTS.PLAN_PRICE(planId), { price });
        return response.data.data;
    },

    archivePlan: async (planId: string) => {
        const response = await apiClient.post<{
            data: { planId: string; code: string; interval: string; subscribersRetained: number };
        }>(BILLING_ADMIN_ENDPOINTS.PLAN_ARCHIVE(planId));
        return response.data.data;
    },

    lookup: async (params: { merchantId: string; storeId: string }) => {
        const response = await apiClient.get<{
            data: { subscription: BillingSubscriptionView; history: BillingHistoryEntry[] };
        }>(BILLING_ADMIN_ENDPOINTS.LOOKUP, { params });
        return response.data.data;
    },

    listDeals: async (params: { merchantId: string; storeId: string }) => {
        const response = await apiClient.get<{ data: { deals: NegotiatedDeal[] } }>(
            BILLING_ADMIN_ENDPOINTS.OVERRIDES,
            { params }
        );
        return response.data.data.deals;
    },

    recordDeal: async (body: RecordDealBody) => {
        const response = await apiClient.post<{ data: Record<string, any> }>(
            BILLING_ADMIN_ENDPOINTS.OVERRIDE,
            body
        );
        return response.data.data;
    },

    removeDeal: async (params: { merchantId: string; storeId: string; planCode: string }) => {
        const response = await apiClient.delete<{ data: { planCode: string; removed: number } }>(
            BILLING_ADMIN_ENDPOINTS.OVERRIDE,
            { params }
        );
        return response.data.data;
    },

    toggleComp: async (body: {
        merchantId: string;
        storeId: string;
        isComped: boolean;
        planCode?: string;
        note: string;
    }) => {
        const response = await apiClient.post<{ data: { isComped: boolean } }>(
            BILLING_ADMIN_ENDPOINTS.COMP,
            body
        );
        return response.data.data;
    },
};
